// @ts-strict;

// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## Blank for Interactive Game
//
// Include description of object & useful info here, please!
import type { PitchClefName } from 'shared/definitions/musicTheory/clefs';
import { isClefName } from 'shared/definitions/musicTheory/clefs';
import type { NoteName } from 'shared/definitions/musicTheory/notesAndAccidentals';
import { isNoteName } from 'shared/definitions/musicTheory/notesAndAccidentals';
import InteractivePrototype from './interactivePrototype';
import type { InteractiveOptions } from './interactivePrototype';

import utils from '../utils';
// clef staff note noteholder measure
import Clef from '../uTheory.Notation/clef';
import Staff from '../uTheory.Notation/staff';
import Note from '../uTheory.Notation/note';
import NoteHolder from '../uTheory.Notation/noteHolder';
import Measure from '../uTheory.Notation/measure';
import Voice from '../uTheory.Notation/voice';
import KeySignature from '../uTheory.Notation/keySignature';
import Music from '../uTheory.Notation/music';
import QuestionGenerator from '../uTheory.Skills/questionGenerator';
import * as Skills from '../uTheory.Skills/skills';
import SkillCalculator from '../uTheory.Skills/skillCalculator';
import type { InteractiveRegistryEntry } from '../uTheory.Skills/interactiveRegistry';

interface NoteIDOptions extends InteractiveOptions {
  clef: PitchClefName;
}

interface NoteIDQuestion {
  note: Note;
  skills: number[];
  text: number[];
}

class NoteID extends InteractivePrototype {
  declare options: NoteIDOptions;

  private skillCalculator: SkillCalculator = new SkillCalculator();

  questions: NoteIDQuestion[] = [];

  declare currentQuestion: NoteIDQuestion;

  clef = new Clef();

  staff = new Staff(this.clef);

  note = new Note();

  noteHolder = new NoteHolder(this.note, 'w');

  measure = new Measure(this.noteHolder);

  voice = new Voice(this.measure);

  keySig = new KeySignature();

  music = new Music(this.staff, this.voice, this.keySig);

  // Public constructor
  constructor(displayDiv: HTMLDivElement, options: NoteIDOptions) {
    options = utils.clone(options, true);
    super(displayDiv, options);

    this.settings = {
      instructions: true,
      notation: {
        fixedWidth: 120,
        ariaTitle: () => {
          if (!this.music) return '';
          const clef = this.music.voices[0]!.getStaff()!.clef;
          const note = this.music.voices[0]!.measures[0]!.noteHolders[0]!.notes[0];
          const lineSpaceString = note ? clef.toLineSpaceString(note) : '';
          return `${clef.toString()} clef: ${lineSpaceString}`;
        },
      },
      letters: {
        letterButtons: true,
        checkButton: false,
      },
      checkMark: {},
      player: {},
      progressTracker: true,
      oneTryOnly: false,
    };

    this.targetSpeedFromSkills = Object.values(NoteID.clefSkills).flat();
    this.init(displayDiv, options);
  }

  init(displayDiv: HTMLDivElement, options: NoteIDOptions) {
    const clefButNoSkill = options.drill && options.clef && !options.skills;
    if (clefButNoSkill) {
      options.skills = NoteID.clefSkills[options.clef];
      if (!options.skills) {
        throw new Error(
          `${
            'A valid options.clef is required ' +
            'to init a noteID interactive without option.skills. ' +
            'options.clef: '
          }${options.clef}`
        );
      }
    }
    if (options.drill) {
      options.maxQuestions = options.maxQuestions || 15;
    }

    // now call the prototype's init function:
    return super.init(displayDiv, options).then(() => {
      this.layout.instructions!.noWrap = true;
    });
  }

  processQuestion(question: NoteName, questionNumber: number): NoteIDQuestion {
    return {
      note: new Note(question),
      skills: [this.calculateSkill(question)],
      text: [questionNumber + 1],
    };
  }

  generateQuestions() {
    // Manual Questions
    if (!this.options.drill && this.options.questions) {
      this.questions = this.options.questions.map((question, questionNumber) =>
        this.processQuestion(question, questionNumber)
      );
    } else {
      this.questions = this.generateDrillQuestions().map((question, questionNumber) =>
        this.processQuestion(question, questionNumber)
      );
    }

    this.questionPrompt = `Click the name of the note you see.<br />#{question} of ${this.questions.length}`;
  }

  initializeMusic() {
    this.clef.setName(this.options.clef || 'treble');
    this.voice.setStaff(this.staff);
  }

  generateDrillQuestions(): NoteName[] {
    const generator = new QuestionGenerator();
    const maxQuestions = this.options.maxQuestions || 10;

    const skills = this.options.skills?.map(Skills.makeString);
    if (!skills || !skills.length) {
      throw Error(
        'NoteID cannot generate drill questions without a provided skills list in options.'
      );
    }

    const skillObjects: { skill: string; probability: number }[] = [];

    skills.forEach((skill) => {
      const skillString = Skills.makeString(skill);
      let probability;
      switch (skillString) {
        case 'treble_c_g':
          probability = 5;
          break;
        case 'treble_a_g':
          probability = 7;
          break;
        case 'treble_low_legers':
          probability = 3;
          break;
        case 'treble_high_legers':
          probability = 2;
          break;
        case 'bass_lines':
          probability = 5;
          break;
        case 'bass_spaces':
          probability = 4;
          break;
        case 'bass_high_legers':
          probability = 4;
          break;
        case 'bass_low_legers':
          probability = 4;
          break;
        // All the other clefs:
        default:
          probability = 7;
          break;
      }
      skillObjects.push({ skill: skillString, probability });
    }, this);
    return generator.multipleSkillsGenerate(skillObjects, maxQuestions, this.options);
  }

  addListeners() {
    this.letterPanel!.addLetterCallback((letter) => this.check(letter as NoteName));
  }

  calculateSkill(noteName: NoteName) {
    const note = new Note(noteName);
    if (!this.skillCalculator) {
      this.skillCalculator = new SkillCalculator();
    }
    return this.skillCalculator.getClefSkill(note, this.clef);
  }

  newQuestion() {
    super.newQuestion();
    this.note.setName(this.currentQuestion.note);
    this.notation!.render();
  }

  check(letter: NoteName) {
    if (this.optionsTrue('testMode')) {
      this.checkpointAnswer(letter);
      return;
    }
    if (this.rightAnswerDelay) return;

    if (this.note.letter === letter) this.rightAnswer(750);
    else this.wrongAnswer();
  }

  // ## CheckPoint Mode Questions:
  newCheckpointQuestion(questionString: string) {
    return this.loadingPromise.then(() => {
      const [noteName, clefName] = questionString.split('/');
      if (!isClefName(clefName)) {
        throw Error(`${clefName} is not a valid clef name in NoteID newCheckpointQuestion`);
      }
      this.clef.setName(clefName);

      if (!isNoteName(noteName)) {
        throw Error(`${noteName} is not a valid note name in NoteID newCheckpointQuestion`);
      }

      this.currentQuestion = this.processQuestion(noteName, 1);

      this.note.setName(noteName);
      this.notation!.render();

      const questionText = 'What is the name of the note below?';
      this.instructionsDiv!.innerHTML = questionText;

      return Promise.resolve(this.currentQuestion);
    });
  }

  getAnswerAndData(letter: NoteName) {
    const correct = this.note.letter === letter;

    const expectedNote = this.note.letter;
    const skill = this.currentQuestion.skills;

    const SVG = this.notation!.exportSVG();

    const answerAndData = {
      question: this.currentQuestion.note.toString(),
      questionType: 'NoteID',
      questionText: this.instructionsDiv!.innerHTML,
      questionSVG: SVG,
      studentAnswerText: letter,
      correctAnswerText: expectedNote,
      rightAnswers: correct ? 1 : 0,
      wrongAnswers: correct ? 0 : 1,
      skills: skill,
    };

    return answerAndData;
  }

  gameOver() {
    this.instructionsDiv!.innerHTML = 'Nice work!';
    this.instructionsDiv!.setAttribute('role', 'alert');
    try {
      this.displayDiv.removeChild(this.lettersDiv!);
    } catch (err) {
      // Swallow it.
    }
    super.gameOver();
  }

  playAnswer() {
    this.player!.play(this.note, { startTime: 0, stopTime: 0.9 });
  }

  static clefSkills: Record<PitchClefName, string[]> = {
    treble: ['treble_c_g', 'treble_a_g', 'treble_low_legers', 'treble_high_legers'],
    bass: ['bass_lines', 'bass_spaces', 'bass_high_legers', 'bass_low_legers'],
    soprano: ['soprano_clef'],
    'mezzo-soprano': ['mezzo_soprano_clef'],
    alto: ['alto_clef'],
    tenor: ['tenor_clef'],
    baritone: ['baritone_clef'],
  };

  static registry: InteractiveRegistryEntry[] = [
    {
      description: 'Identify a note on the treble clef staff',
      skills: NoteID.clefSkills.treble,
      options: {
        clef: 'treble',
      },
    },
    {
      description: 'Identify a note on the bass clef staff',
      skills: NoteID.clefSkills.bass,
      options: {
        clef: 'bass',
      },
    },
    {
      description: 'Name soprano clef notes',
      skills: NoteID.clefSkills.soprano,
      options: {
        clef: 'soprano',
      },
    },
    {
      description: 'Name mezzo-soprano clef notes',
      skills: NoteID.clefSkills['mezzo-soprano'],
      options: {
        clef: 'mezzo-soprano',
      },
    },
    {
      description: 'Name alto clef notes',
      skills: NoteID.clefSkills.alto,
      options: {
        clef: 'alto',
      },
    },
    {
      description: 'Name tenor clef notes',
      skills: NoteID.clefSkills.tenor,
      options: {
        clef: 'tenor',
      },
    },
    {
      description: 'Name baritone clef notes',
      skills: NoteID.clefSkills.baritone,
      options: {
        clef: 'baritone',
      },
    },
  ];
}

export default NoteID;
